const yup = require('yup')
const mobileUserTypes = require('../mobile-user-types')
const levelsOfCare = require('../levels-of-care')
const { celebrationDateTypeValues } = require('../celebration-date-types')
const { significantDateTypeValues } = require('../significant-date-types')
const { labelify } = require('./helpers')

const celebrationDateShape = {
  type: yup.string().oneOf(celebrationDateTypeValues).required().default('wedding-anniversary').label('Type'),
  date: yup.date().required().default(null).label('Date'),
  label: yup
    .string()
    .ensure()
    .when('type', { is: 'other', then: (s) => s.required('Label is required for "Other" type.') })
    .label('Label'),
}

const celebrationDateSchema = yup.object().shape(celebrationDateShape)

const significantDateShape = {
  type: yup.string().oneOf(significantDateTypeValues).required().default('spouse-partner-passing').label('Type'),
  date: yup.date().required().default(null).label('Date'),
  label: yup
    .string()
    .ensure()
    .when('type', { is: 'other', then: (s) => s.required('Label is required for "Other" type.') })
    .label('Label'),
}

const significantDateSchema = yup.object().shape(significantDateShape)

const userTypes = yup.array().ensure().of(yup.string().oneOf(mobileUserTypes))

const shape = labelify({
  mobileUser: yup.mixed(),
  photo: yup.mixed(),
  name: yup.object().shape({
    display: yup.string().ensure().label('Name'),
    first: yup.string().ensure(),
    last: yup.string().ensure(),
  }),
  address: yup
    .object({
      street1: yup.string(),
      street2: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zip: yup.string(),
      country: yup.string(),
    })
    .optional()
    .transform((v) => (v === null ? undefined : v)),
  addressCoordinates: yup
    .object()
    .shape({
      latitude: yup.number().nullable(),
      longitude: yup.number().nullable(),
    })
    .optional(),
  display: yup.object().shape(
    labelify({
      departmentName: yup.string().ensure(),
    })
  ),
  staffAppSectionsEnabled: yup.object().shape(
    labelify({
      alerts: yup.bool().default(false),
      lifts: yup.bool().default(false),
      trails: yup.bool().default(false),
      stats: yup.bool().default(false),
      vehicles: yup.bool().default(false),
      locations: yup.bool().default(false),
    })
  ),
  hiddenCalendars: yup.array().of(yup.mixed()).default([]),
  isAllEventsTabHidden: yup.bool().default(false),
  hiddenMealLocations: yup.array().of(yup.mixed()).default([]),
  isAllMealLocationsTabHidden: yup.bool().default(false),
  trackAttendance: yup.bool().default(false),
  photosPermitted: yup.bool().default(true),
  mediaReleases: yup.object().shape({
    photo: yup.date().nullable().default(null),
    socialMedia: yup.date().nullable().default(null),
  }),
  phone: yup.string().ensure(),
  notes: yup.string().ensure(),
  interest: yup.string().ensure(),
  servedInMilitary: yup.bool().default(false),
  militaryBranch: yup
    .string()
    .ensure()
    .when('servedInMilitary', { is: true, then: (s) => s.required() }),
  includeInDirectory: yup.bool(),
  staffTitle: yup.mixed(),
  awardCount: yup.number().default(0),
  userTypes,
  requestedUserTypes: yup.array().nullable(),
  associatedWith: yup.string(),
  internalNotes: yup.string().max(500, 'Internal notes must be less than 500 characters.').nullable(),
  levelOfCare: yup.string().oneOf(levelsOfCare).nullable().default(''),
  birthdate: yup.date().max(new Date(), 'Please enter a valid date in the past.').nullable().default(null),
  moveInDate: yup.date().nullable().default(null),
  hireDate: yup.date().nullable().default(null),
  birthdateAcknowledgedAt: yup.number().nullable(),
  hasOptedOutOfBirthdayAnnouncement: yup.bool().default(false),
  celebrationDates: yup.array().of(yup.object().shape(celebrationDateShape)).default([]),
  significantDates: yup.array().of(yup.object().shape(significantDateShape)).default([]),
  packages: yup.array().of(yup.string()).default([]),
  product: yup.string().nullable(),
  connectionScore: yup.mixed(),
})

const deletedShape = {
  deleted: yup.boolean(),
  deletedAt: yup.date().nullable(),
  deletedBy: yup.mixed().nullable(),
}

const profileSchemaPartial = yup.object().shape(shape).shape(deletedShape)
const profileSchema = profileSchemaPartial

const profileFormSchema = yup
  .object()
  .shape(shape)
  .shape({
    birthdate: yup.mixed().datetime().default(undefined),
    moveInDate: yup.mixed().datetime().default(undefined),
    hireDate: yup.mixed().datetime().default(undefined),
    mediaReleases: yup.object().shape({
      photo: yup.mixed().datetime(undefined),
      socialMedia: yup.mixed().datetime(undefined),
    }),
    celebrationDates: yup
      .array()
      .of(yup.object().shape({ ...celebrationDateShape, date: yup.mixed().datetime().required().label('Date') }))
      .default([]),
    significantDates: yup
      .array()
      .of(yup.object().shape({ ...significantDateShape, date: yup.mixed().datetime().required().label('Date') }))
      .default([]),
    address: yup.object({
      street1: yup.string().ensure(),
      street2: yup.string().ensure(),
      city: yup.string().ensure(),
      state: yup.string().ensure(),
      zip: yup.string().ensure(),
      country: yup.string().ensure(),
    }),
    packages: yup.array().of(yup.string()).default([]),
  })

const profileSchemaUserTypes = yup.object().shape({
  userTypes,
})

const profileSchemaDeleted = yup.object().shape(deletedShape)

const profileResponseSchema = profileSchema.shape({
  resort: yup.mixed(),
  mobileUserEmail: yup.string().nullable(),
})

module.exports = {
  profileSchema,
  profileSchemaPartial,
  profileFormSchema,
  profileSchemaUserTypes,
  profileSchemaDeleted,
  profileResponseSchema,
  celebrationDateSchema,
  significantDateSchema,
}
